import { useEffect, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Dot, ReferenceLine
} from 'recharts';
import { getWeight, getDietChanges } from './api';
import SectionHeader from './SectionHeader';

const PERIOD_OPTIONS = [
  { label: '3 мес', value: 3 },
  { label: '6 мес', value: 6 },
  { label: '12 мес', value: 12 },
];

export default function WeightBlock({ petId, pdfPath, pdfName, docs, onDocsClick }) {
  const [months, setMonths] = useState(3);
  const [data, setData] = useState([]);
  const [dietChanges, setDietChanges] = useState([]); // [{date, food_name}]
  const [loading, setLoading] = useState(true);
  const [activeChange, setActiveChange] = useState(null); // {date, food_name}

  useEffect(() => {
    setLoading(true);
    setActiveChange(null);
    Promise.all([
      getWeight(petId, months),
      getDietChanges(petId),
    ])
      .then(([w, dc]) => { setData(w); setDietChanges(dc); })
      .catch(() => { setData([]); setDietChanges([]); })
      .finally(() => setLoading(false));
  }, [petId, months]);

  const periodTabs = (
    <div className="visits-period-tabs" style={{ marginBottom: 12 }}>
      {PERIOD_OPTIONS.map(opt => (
        <button
          key={opt.value}
          className={`visits-tab${months === opt.value ? ' visits-tab-active' : ''}`}
          onClick={() => setMonths(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );

  if (loading) return null;

  if (data.length === 0) return (
    <section className="card-section" id="section-weight">
      <SectionHeader
        title="Вес"
        pdfPath={pdfPath}
        pdfName={pdfName}
        periodMode="days60_years"
        sectionKey="weight"
        docs={docs}
        onDocsClick={onDocsClick}
      />
      {periodTabs}
      <p style={{ color: '#888', fontSize: 14, margin: 0 }}>Нет данных</p>
    </section>
  );

  const last = data[data.length - 1];
  const weights = data.map(d => d.weight);
  const minW = Math.min(...weights);
  const maxW = Math.max(...weights);
  const padding = (maxW - minW) * 0.15 || 0.5;
  const yMin = Math.max(0, +(minW - padding).toFixed(2));
  const yMax = +(maxW + padding).toFixed(2);

  const parseD = (s) => { const [d, m, y] = s.split('.'); return new Date(2000 + +y, +m - 1, +d); };
  const weightDateSet = new Set(data.map(d => d.date));
  const firstDt = parseD(data[0].date);
  const lastDt = parseD(data[data.length - 1].date);

  const changesInRange = dietChanges.filter(dc => {
    const dt = parseD(dc.date);
    return dt >= firstDt && dt <= lastDt;
  });

  const extraDates = changesInRange
    .filter(dc => !weightDateSet.has(dc.date))
    .map(dc => dc.date);

  const chartData = [
    ...data,
    ...extraDates.map(d => ({ date: d, weight: null })),
  ].sort((a, b) => parseD(a.date) - parseD(b.date));

  const handleLineClick = (dc) => {
    setActiveChange(prev => prev?.date === dc.date ? null : dc);
  };

  return (
    <section className="card-section" id="section-weight">
      <SectionHeader
        title="Вес"
        pdfPath={pdfPath}
        pdfName={pdfName}
        periodMode="days60_years"
        sectionKey="weight"
        docs={docs}
        onDocsClick={onDocsClick}
      />

      {periodTabs}

      <div style={{ marginBottom: 16 }}>
        <span style={{ fontSize: 15, fontWeight: 600, color: '#2d2d2d' }}>
          {last.weight} кг
        </span>
        <span style={{ fontSize: 13, color: '#888', marginLeft: 8 }}>
          на {last.date}
        </span>
      </div>

      {data.length > 1 && (
        <ResponsiveContainer width="100%" height={180}>
          <LineChart data={chartData} margin={{ top: 24, right: 8, left: -16, bottom: 32 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0ebe3" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 11, fill: '#888' }}
              angle={-45}
              textAnchor="end"
              interval={0}
              tickFormatter={(v) => weightDateSet.has(v) ? v : ''}
            />
            <YAxis
              domain={[yMin, yMax]}
              tick={{ fontSize: 11, fill: '#888' }}
              tickFormatter={v => `${v}`}
            />
            <Tooltip
              formatter={(v) => v != null ? [`${v} кг`, 'Вес'] : [null, null]}
              contentStyle={{ borderRadius: 8, fontSize: 13 }}
            />
            <Line
              type="monotone"
              dataKey="weight"
              stroke="#8b7355"
              strokeWidth={2}
              dot={<Dot r={4} fill="#8b7355" />}
              activeDot={{ r: 6 }}
              connectNulls
            />
            {changesInRange.map(dc => (
              <ReferenceLine
                key={dc.date}
                x={dc.date}
                stroke={activeChange?.date === dc.date ? '#2d5a1b' : '#4B643B'}
                strokeWidth={activeChange?.date === dc.date ? 2.5 : 1.5}
                strokeDasharray="4 2"
                label={{ value: '🥩', position: 'top', fontSize: 14 }}
                onClick={() => handleLineClick(dc)}
                style={{ cursor: 'pointer' }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}

      {/* Попап смены рациона */}
      {activeChange && (
        <div className="weight-diet-popup">
          <span className="weight-diet-popup-icon">🥩</span>
          <div className="weight-diet-popup-text">
            <div className="weight-diet-popup-date">{activeChange.date}</div>
            <div className="weight-diet-popup-name">{activeChange.food_name}</div>
          </div>
          <button
            className="weight-diet-popup-close"
            onClick={() => setActiveChange(null)}
          >
            ✕
          </button>
        </div>
      )}

      {changesInRange.length > 0 && (
        <p className="weight-diet-hint">
          🥩 — смена рациона/корма, нажмите на линию для справки
        </p>
      )}
    </section>
  );
}
